<template>
  <div class="meeting">
    <div class="meeting__top">
      <meeting-toolbar class="pte--auto" />
      <a-spacer />
      <meeting-configuration />
      <meeting-leave-button />
    </div>
    <div
      v-if="
        state.matches('participants.visible') ||
        state.matches('participantsNetwork.visible') ||
        state.matches('mobileUtilityMenu.visible') ||
        state.matches('notifyDataLimit.visible')
      "
      class="meeting__overlay"
    ></div>

    <meeting-lobby v-if="meetingStore.isLobby" />
    <template v-if="meetingStore.isCalling">
      <call-video-local v-if="user.isClient" />
      <call-video-remote v-else="user.isClient" />
    </template>
    <pip-view v-if="meetingStore.isDraw && !user.isClient" />
    <template v-if="!meetingStore.isLobby">
      <meeting-board-client v-if="user.isClient" :over="meetingStore.isCalling || isScreenSharing" />
      <template v-else>
        <meeting-board :over="meetingStore.isCalling || isScreenSharing" />
        <meeting-overlay v-if="!session.notClientPresent && !session.notClientCamera" />
        <meeting-drop-area />
      </template>
    </template>

    <TransitionGroup name="slide-left">
      <a-toast
        v-if="state.matches('roleExchange.waitingForCounterpartyResponse')"
        type="info"
        :action="$t('meeting.toast.action.cancel')"
        loading
        overlay
        @action="roleExchangeProcedure.cancel"
        >{{ $t('meeting.roleExchange.waitingMessage') }}</a-toast
      >
      <a-toast v-if="state.matches('roleExchange.counterpartyAcceptedOffer')" type="success">{{
        $t('meeting.roleExchange.acceptedMessage')
      }}</a-toast>
      <a-toast v-if="state.matches('roleExchange.counterpartyDeniedOffer')" type="danger">{{
        $t('meeting.roleExchange.deniedMessage')
      }}</a-toast>
      <a-toast
        v-if="
          state.matches('roleExchange.offerCanceled') &&
          roleExchangeProcedure.context.counterparty.party === 'assistant'
        "
        type="info"
        >{{ $t('meeting.roleExchange.canceledMessage') }}</a-toast
      >
      <a-toast v-if="state.matches('roleExchange.partyRoleChanged')" type="info">{{
        $t('meeting.roleExchange.changedRoleMessage', {name: roleExchangeProcedure.context.role})
      }}</a-toast>

      <a-toast
        v-if="notifyDataLimit80 === NotificationState.SHOW && !meetingStore.isLobby"
        position="top-right"
        type="warning"
        >{{ $t('meeting.alert.notifyDataLimit80') }}</a-toast
      >
      <a-toast
        v-if="notifyDataLimit100 === NotificationState.SHOW && !meetingStore.isLobby"
        position="top-right"
        type="warning"
        hideMessage
        >{{ $t('meeting.alert.notifyDataLimit') }}</a-toast
      >
      <a-toast v-if="networkState.matches('connectionNetwork.offline')" type="warning-network">{{
        isReconnecting
          ? $t('meeting.notification.reconnecting', {defaultValue: 'Connection lost, reconnecting...'})
          : $t('meeting.notification.lostConnection', {role: roleLeave})
      }}</a-toast>
      <a-toast
        v-if="networkState.matches('connectionNetwork.online') && !roleLeave"
        type="success-network"
        class="toast-reconnect-succeed"
        >{{ $t('meeting.notification.userReconnected') }}</a-toast
      >
      <a-toast
        v-if="networkState.matches('connectionNetwork.online') && roleLeave && user.party !== roleLeave"
        type="success-network"
        class="toast-reconnect-succeed"
        >{{ $t('meeting.notification.participantReconnected', {role: roleLeave}) }}</a-toast
      >
    </TransitionGroup>
    <Transition name="fade">
      <a-modal
        v-if="state.matches('roleExchange.waitingForPartyResponse')"
        :title="$t('meeting.roleExchange.modalTitle')"
        @close="roleExchangeProcedure.denyOffer"
        closable
      >
        <p v-if="state.matches('roleExchange.waitingForPartyResponse')">
          {{ $t('meeting.roleExchange.offerMessage', {role: roleExchangeProcedure.context.counterparty.party}) }}
        </p>
        <template #footer>
          <a-button variant="outline-primary" @click="roleExchangeProcedure.denyOffer">{{
            $t('meeting.roleExchange.denyMessage')
          }}</a-button>
          <a-button variant="primary" @click="roleExchangeProcedure.acceptOffer">{{
            $t('meeting.roleExchange.acceptMessage')
          }}</a-button>
        </template>
      </a-modal>
    </Transition>
    <meeting-transcript />
    <div class="meeting__bottom">
      <meeting-bottom-toolbar v-if="user.isAssistant && !meetingStore.isLobby" />
    </div>
    <meeting-client-tools class="client-tools" v-if="user.isClient" />
    <meeting-snapshot-overlay v-if="snapshot.loading.value" />
    <meeting-notification />
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {useUserStore, useSessionStore} from '@/stores'
import {useMeetingStore} from '@/stores'
import {useMeetingMachine} from '@/state'
import {useWearML} from '@/composables'
import {useWakeLock} from '@vueuse/core'
import useNetworkMachine from '@/state/useNetworkMachine'
import AToast from '@/components/AToast.vue'
import {useRoleExchange} from '@/procedures/useRoleExchange'
import {useParticipantDisconnected} from '@/procedures/useParticipantDisconnected'
import {useChatCallback} from '@/procedures/useChat'
import {useSnapshot} from '@/composables/useSnapshot'
import {storeToRefs} from 'pinia'
import {useStreamStore} from '@/stores/streamStore'
import {useTranslation} from 'i18next-vue'
import {useMessageStore} from '@/stores/messagesStore'
import {useScreenShareStore} from '@/stores/screenshareStore'

const messageStore = useMessageStore()
const streamStore = useStreamStore()
const {cameraAccess, microphoneAccess} = storeToRefs(streamStore)
const meetingStore = useMeetingStore()
const user = useUserStore()
const session = useSessionStore()
const {state} = useMeetingMachine()
const wearML = useWearML()
const {request, release} = useWakeLock()
const roleExchangeProcedure = useRoleExchange()
const {state: networkState} = useNetworkMachine()
const {roleLeave: lastRoleLeave} = useParticipantDisconnected()
const {isScreenSharing} = storeToRefs(useScreenShareStore())
const {t} = useTranslation()
useChatCallback()
const snapshot = useSnapshot()
// Enum for notification states
const NotificationState = Object.freeze({
  HIDE: 'hide',
  SHOW: 'show',
  SHOWED: 'showed',
})
// variable to notify user about 100% of data limit
const notifyDataLimit100 = ref(NotificationState.HIDE)
// variable to notify user about 80% of data limit
const notifyDataLimit80 = ref(NotificationState.HIDE)

function showDataLimitNotification(notifyRef, shouldNotify) {
  // Only show if currently hidden and condition is met
  if (notifyRef.value === NotificationState.HIDE && shouldNotify) {
    notifyRef.value = NotificationState.SHOW
    // After 2 seconds, mark as 'showed' to prevent repeat notifications
    setTimeout(() => {
      notifyRef.value = NotificationState.SHOWED
    }, 2000)
    return true
  }
  return false
}

const roleLeave = computed(() => {
  const participantRole = user.party + '_' + user.id
  if (session.participantsLeave.some(res => res === participantRole)) {
    lastRoleLeave.value = ''
    return 'You'
  } else {
    return lastRoleLeave.value
  }
})
// Our own connection dropped and is being re-opened: the user should wait, not leave
const isReconnecting = computed(() => roleLeave.value === 'You')

watch(user, () => {
  if (user.dataLimit > 0) {
    const used = user.consumptionData
    // Notify at 100% data limit
    if (showDataLimitNotification(notifyDataLimit100, used >= user.dataLimit)) return
    // Notify at 80% data limit
    showDataLimitNotification(notifyDataLimit80, used >= 0.8 * user.dataLimit)
  }
})
onMounted(() => {
  request('screen')
  wearML.refresh()
})
onUnmounted(() => {
  release()
})
onMounted(async () => {
  
  if ((microphoneAccess.value ==='prompt' || cameraAccess.value === 'prompt') && user.isClient) {
    messageStore.addMessage({
      text: t(
        'meeting.notification.allowPermission',
        "Please tap 'Allow' so the support agent can hear you and see what you need help with."
      ),
      promise: streamStore.ensurePermissions(),
    })
  }
})
</script>

<style lang="scss">
.meeting {
  .toast-reconnect-succeed {
    @media (max-width: 500px) {
      font-size: 13px;
    }
  }

  &__top {
    display: flex;
    position: fixed;
    width: 100%;
    padding: $spacing-s $spacing-xs;
    z-index: ($zindex-overlay + 2);
    pointer-events: none;
    gap: 5px;
    justify-content: flex-end;
  }
  &__overlay {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: $zindex-overlay;
  }
}
.meeting__bottom {
  position: fixed;
  width: 100%;
  z-index: ($zindex-overlay + 2);
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: 12px;
}
.client-tools {
  position: fixed;
  z-index: ($zindex-overlay + 2);
  right: 10px;
  bottom: 10px;
}
</style>
